import { useState, useRef, useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useIngredientAutocomplete } from './useIngredientAutocomplete'
import type { RecipeIngredientFormData } from './types'

interface IngredientAutocompleteProps {
  value: RecipeIngredientFormData
  onChange: (val: RecipeIngredientFormData) => void
  index: number
}

export function IngredientAutocomplete({ value, onChange }: IngredientAutocompleteProps) {
  const { t } = useTranslation('recipes')
  const [inputValue, setInputValue] = useState(value.ingredientName ?? '')
  const [debouncedQuery, setDebouncedQuery] = useState('')
  const [open, setOpen] = useState(false)
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const showUnitInput = useMemo(
    () => !!inputValue && !value.ingredientId,
    [inputValue, value.ingredientId],
  )

  const { data: suggestions = [] } = useIngredientAutocomplete(debouncedQuery)

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current)
    debounceRef.current = setTimeout(() => {
      setDebouncedQuery(inputValue)
    }, 300)
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current)
    }
  }, [inputValue])

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newVal = e.target.value
    setInputValue(newVal)
    setOpen(newVal.length >= 1)
    onChange({ ...value, ingredientName: newVal, ingredientId: null })
  }

  const handleSelect = (suggestion: { id: string | null; name: string; canonicalUnit: string }) => {
    setInputValue(suggestion.name)
    setOpen(false)
    onChange({
      ...value,
      ingredientId: suggestion.id,
      ingredientName: suggestion.name,
      canonicalUnit: suggestion.canonicalUnit,
      unit: suggestion.canonicalUnit,
    })
  }

  const handleBlur = () => {
    setTimeout(() => setOpen(false), 150)
  }

  return (
    <div className="relative flex-1">
      <input
        type="text"
        value={inputValue}
        onChange={handleInputChange}
        onBlur={handleBlur}
        onFocus={() => inputValue.length >= 1 && setOpen(true)}
        role="combobox"
        aria-expanded={open}
        aria-autocomplete="list"
        aria-label={t('form.ingredientNameLabel')}
        placeholder={t('form.ingredientPlaceholder')}
        className="w-full rounded-lg border border-line bg-surface px-3 py-2 text-sm text-ink focus:outline-none focus:ring-2 focus:ring-accent min-h-[44px]"
      />
      {open && (
        <ul
          role="listbox"
          className="absolute z-10 mt-1 w-full max-h-48 overflow-y-auto bg-surface border border-line rounded-lg shadow-lg"
        >
          {suggestions.map((s) => (
            <li key={s.id ?? s.name}>
              <button
                type="button"
                role="option"
                aria-selected={value.ingredientId === s.id}
                onMouseDown={() => handleSelect(s)}
                className="w-full text-left px-3 py-2 text-sm hover:bg-accent-tint min-h-[44px] flex items-center"
              >
                <span>{s.name}</span>
                <span className="ml-auto text-xs text-muted">{s.canonicalUnit}</span>
              </button>
            </li>
          ))}
          {suggestions.length === 0 && inputValue.length >= 1 && (
            <li>
              <button
                type="button"
                onMouseDown={() =>
                  handleSelect({ id: null, name: inputValue, canonicalUnit: '' })
                }
                className="w-full text-left px-3 py-2 text-sm hover:bg-accent-tint min-h-[44px] flex items-center text-accent"
              >
                {t('form.addNewIngredient', { name: inputValue })}
              </button>
            </li>
          )}
        </ul>
      )}
      {showUnitInput && !value.ingredientId && (
        <div className="mt-2">
          <label className="block">
            <span className="text-sm font-medium text-ink">{t('form.canonicalUnitLabel')}</span>
            <input
              type="text"
              value={value.canonicalUnit}
              onChange={(e) => onChange({ ...value, canonicalUnit: e.target.value, unit: e.target.value })}
              placeholder={t('form.canonicalUnitPlaceholder')}
              className="mt-1 w-full rounded-lg border border-line bg-surface px-3 py-2 text-sm text-ink focus:outline-none focus:ring-2 focus:ring-accent min-h-[44px]"
            />
          </label>
          <p className="text-xs text-ink/60 mt-1">{t('form.canonicalUnitHelper')}</p>
        </div>
      )}
    </div>
  )
}
